import React, { useEffect, useState } from "react";
import Select from "react-select";
import { addLocation } from "helpers/fakebackend_helper";
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Label,
    Input,
    Button,
    InputGroup,
    InputGroupText,
    Row,
    Col
} from "reactstrap";
import usStates from "constants/usStates";
import daysOfWeek from "constants/daysOfWeek";
import fileTypes from "constants/fileTypes";
import initialFormState from "constants/initialLocationFormState";

const AddLocationModal = ({ isOpen, handleSave, toggle, accountManagers, thirdPartyPlatforms, parkingTypes }) => {
    const user = JSON.parse(localStorage.getItem("authUser"));
    const [formData, setFormData] = useState(initialFormState);
    const [isSaving, setIsSaving] = useState(false);

    // Update location_address whenever address components change
    useEffect(() => {
        const { street_address, city, state, zip_code } = formData;
        const addressParts = [street_address, city, state].filter(Boolean);
        let fullAddress = addressParts.join(", ");
        if (zip_code) {
            fullAddress = `${fullAddress} ${zip_code}`;
        }
        setFormData(prev => ({ ...prev, location_address: fullAddress }));
    }, [formData.street_address, formData.city, formData.state, formData.zip_code]);

    // Update location_hours whenever hours data changes
    useEffect(() => {
        const { hours } = formData;
        const hoursEntries = [];

        daysOfWeek.forEach(day => {
            if (hours[day.id].active) {
                const openTime = hours[day.id].open || "Closed";
                const closeTime = hours[day.id].close || "Closed";
                hoursEntries.push(`${day.label}: ${openTime} - ${closeTime}`);
            }
        });

        // use new line instead of comma
        const formattedHours = hoursEntries.join("\n");
        setFormData(prev => ({ ...prev, location_hours: formattedHours }));
    }, [formData.hours]);

    const handleHoursChange = (day, field, value) => {
        setFormData(prev => ({
            ...prev,
            hours: {
                ...prev.hours,
                [day]: {
                    ...prev.hours[day],
                    [field]: value
                }
            }
        }));
    };

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const resetForm = () => {
        setFormData(initialFormState);
    };

    const onSave = async () => {
        const fd = new FormData();

        // Normal fields
        fd.append("location_name", formData.location_name || "");
        fd.append("location_address", formData.location_address || "");
        fd.append("location_hours", formData.location_hours || "");
        fd.append("tax_status", formData.tax_status || "");
        fd.append("lease_amount", formData.lease_amount || "");
        fd.append("location_type", formData.location_type || "");
        fd.append("location_spaces", formData.location_spaces || "");
        fd.append("invoicing_type", formData.invoicing_type || "");
        fd.append("account_manager_id", formData.account_manager_id || "");
        fd.append("is_active", formData.is_active ?? 0);
        fd.append("stage", formData.stage || "");
        fd.append("operating_structure", formData.operating_structure || "");
        fd.append("monthly_parking_capacity", formData.monthly_parking_capacity ?? 0);
        fd.append("is_cash_accepted", formData.is_cash_accepted ?? 0);
        fd.append("is_monthly_parking_active", formData.is_monthly_parking_active ?? 0);
        fd.append("partner_remittance_terms", formData.partner_remittance_terms || "");
        fd.append("partner_remittance_amount", formData.partner_remittance_amount || 0);
        fd.append("partner_remittance_percent", formData.partner_remittance_percent || 0);
        fd.append("labor_hours_cap", formData.labor_hours_cap || 0);
        fd.append("invoice_directives", formData.invoice_directives || "");
        fd.append("manager_labor_rate", formData.manager_labor_rate || 0);
        fd.append("attendant_labor_rate", formData.attendant_labor_rate || 0);
        fd.append("is_curbads_location", formData.is_curbads_location || 0);
        fd.append("management_fee", formData.management_fee || 0);
        fd.append("insurance_fee", formData.insurance_fee || 0);
        fd.append("parking_lot_fee", formData.parking_lot_fee || 0);
        fd.append("permit_meter_fee", formData.permit_meter_fee || 0);
        fd.append("tech_fee", formData.tech_fee || 0);
        fd.append("ops_supplies_fee", formData.ops_supplies_fee || 0);
        fd.append("partner_remittance_fee", formData.partner_remittance_fee || 0);
        fd.append("parking_lot_cost", formData.parking_lot_cost || 0);

        formData.fileSections.forEach(section => {
            section.files.forEach(file => {
                fd.append(`${section.type}[]`, file);
            });
        });

        // Finance terms
        (formData.finance_terms || []).forEach(term => {
            fd.append("finance_terms[]", term.term || "");
        });

        // Third party platforms
        (formData.third_party_platforms || []).forEach(platform => {
            fd.append("third_party_platforms[]", platform.id || "");
        });

        // Parking types
        (formData.parking_types || []).forEach(type => {
            fd.append("parking_types[]", type.id || "");
        });

        // Parking lots
        (formData.parking_lots || []).forEach((lot, index) => {
            fd.append(`parking_lots[${index}][type]`, lot.type || "");
            fd.append(`parking_lots[${index}][access_hours]`, lot.access_hours || "");
            fd.append(`parking_lots[${index}][rent_amount]`, lot.rent_amount || "");
            fd.append(`parking_lots[${index}][due_date]`, lot.due_date || "");
            fd.append(`parking_lots[${index}][payee_name]`, lot.payee_name || "");
            fd.append(`parking_lots[${index}][payee_email]`, lot.payee_email || "");
            fd.append(`parking_lots[${index}][payee_phone]`, lot.payee_phone || "");
            fd.append(`parking_lots[${index}][spaces]`, lot.spaces || 0);
            fd.append(`parking_lots[${index}][rules]`, lot.rules || "");
        });

        setIsSaving(true);
        try {
            await addLocation(user.token, fd);
            handleSave(fd);
            resetForm()
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="lg">
            <ModalHeader toggle={toggle}>Add Location</ModalHeader>
            <ModalBody>

                <Label>Location Name</Label>
                <Input
                    type="text"
                    value={formData.location_name}
                    onChange={e => handleChange("location_name", e.target.value)}
                />

                <Row className="mt-3">
                    <Col md={12}>
                        <Label>Street Address</Label>
                        <Input
                            type="text"
                            value={formData.street_address}
                            onChange={e => handleChange("street_address", e.target.value)}
                            placeholder="123 Main St"
                        />
                    </Col>
                </Row>

                <Row>
                    <Col md={5}>
                        <Label className="mt-3">City</Label>
                        <Input
                            type="text"
                            value={formData.city}
                            onChange={e => handleChange("city", e.target.value)}
                            placeholder="City"
                        />
                    </Col>
                    <Col md={3}>
                        <Label className="mt-3">State</Label>
                        <Input
                            type="select"
                            value={formData.state}
                            onChange={e => handleChange("state", e.target.value)}
                        >
                            <option value="">Select State</option>
                            {usStates.map(state => (
                                <option key={state.value} value={state.value}>
                                    {state.label}
                                </option>
                            ))}
                        </Input>
                    </Col>
                    <Col md={4}>
                        <Label className="mt-3">Zip Code</Label>
                        <Input
                            type="text"
                            value={formData.zip_code}
                            onChange={e => handleChange("zip_code", e.target.value)}
                            placeholder="12345"
                        />
                    </Col>
                </Row>

                {/* Preview of the combined address */}
                {formData.location_address && (
                    <Row>
                        <Col md={12}>
                            <Label className="mt-3">Full Address Preview</Label>
                            <Input
                                type="text"
                                value={formData.location_address}
                                readOnly
                                style={{ backgroundColor: '#f8f9fa' }}
                            />
                        </Col>
                    </Row>
                )}

                {/* Hours section */}
                <Label className="mt-3">Hours</Label>
                {daysOfWeek.map(day => (
                    <Row key={day.id} className="mb-2">
                        <Col md={12}>
                            <div className="d-flex align-items-center">
                                <Input
                                    type="checkbox"
                                    id={`${day.id}-active`}
                                    checked={formData.hours[day.id].active}
                                    onChange={e => handleHoursChange(day.id, "active", e.target.checked)}
                                    className="me-2"
                                />
                                <Label className="me-3 mb-0" style={{ minWidth: '80px' }}>
                                    {day.label}
                                </Label>
                                <Input
                                    type="text"
                                    value={formData.hours[day.id].open}
                                    onChange={e => handleHoursChange(day.id, "open", e.target.value)}
                                    disabled={!formData.hours[day.id].active}
                                    className="me-2"
                                    style={{ maxWidth: "120px" }}
                                />
                                <span className="mx-2">to</span>
                                <Input
                                    type="text"
                                    value={formData.hours[day.id].close}
                                    onChange={e => handleHoursChange(day.id, "close", e.target.value)}
                                    disabled={!formData.hours[day.id].active}
                                    className="ms-2"
                                    style={{ maxWidth: "120px" }}
                                />
                            </div>
                        </Col>
                    </Row>
                ))}

                {/* Preview of the combined hours */}
                {formData.location_hours && (
                    <Row>
                        <Col md={12}>
                            <Label className="mt-3">Hours Preview</Label>
                            <Input
                                type="textarea"
                                value={formData.location_hours}
                                readOnly
                                rows="7" // make taller so all days show
                                style={{ backgroundColor: "#f8f9fa", whiteSpace: "pre-line" }}
                            />
                        </Col>
                    </Row>
                )}

                <Row>
                    <Col md={6}>
                        <Label className="mt-3">Account Manager</Label>
                        <Input
                            type="select"
                            value={formData.account_manager_id}
                            onChange={e => handleChange("account_manager_id", e.target.value)}
                        >
                            {accountManagers.map((item, index) => (
                                <option value={item.id} key={index}>{item.full_name}</option>
                            ))}
                        </Input>
                    </Col>
                    <Col md={6}>
                        <Label className="mt-3">Property Type</Label>
                        <Input
                            type="select"
                            value={formData.location_type}
                            onChange={e => handleChange("location_type", e.target.value)}
                        >
                            <option value={"Hotel"}>Hotel</option>
                            <option value={"Office Building"}>Office Building</option>
                            <option value={"Apartment / Condo"}>Apartment / Condo</option>
                            <option value={"Restaurant"}>Restaurant</option>
                            <option value={"Retail Center"}>Retail Center</option>
                            <option value={"Health Club"}>Health Club</option>
                            <option value={"Airport"}>Airport</option>
                            <option value={"Parking Lot"}>Parking Lot</option>
                        </Input>
                    </Col>
                </Row>

                <Row>
                    <Col md={6}>
                        <Label className="mt-3">Invoicing Type</Label>
                        <Input
                            type="select"
                            value={formData.invoicing_type}
                            onChange={e => handleChange("invoicing_type", e.target.value)}
                        >
                            <option value="Variable">Variable</option>
                            <option value="Flat">Flat</option>
                            <option value="No Invoice">No Invoice</option>
                        </Input>
                    </Col>
                    <Col md={6}>
                        <Label className="mt-3">Stage</Label>
                        <Input
                            type="select"
                            value={formData.stage}
                            onChange={e => handleChange("stage", e.target.value)}
                        >
                            <option value="Live">Live</option>
                            <option value="Lost">Lost</option>
                            <option value="Closed">Closed</option>
                            <option value="Lead">Lead</option>
                            <option value="Proposal Sent">Proposal Sent</option>
                            <option value="Contract Sent">Contract Sent</option>
                            <option value="Onboarding">Onboarding</option>
                        </Input>
                    </Col>
                </Row>

                <Row>
                    <Col md={6}>
                        <Label className="mt-3">Operating Structure</Label>
                        <Input
                            type="select"
                            value={formData.operating_structure}
                            onChange={e => handleChange("operating_structure", e.target.value)}
                        >
                            <option value="Lease">Lease</option>
                            <option value="Non Lease">Non Lease</option>
                        </Input>
                    </Col>
                    <Col md={6}>
                        <Label className="mt-3">Status</Label>
                        <Input
                            type="select"
                            value={formData.is_active}
                            onChange={e => handleChange("is_active", e.target.value)}
                        >
                            <option value={1}>Active</option>
                            <option value={0}>Inactive</option>
                        </Input>
                    </Col>
                </Row>

                <Row>
                    <Col md={6}>
                        <Label className="mt-3">Cash Accepted</Label>
                        <Input
                            type="select"
                            value={formData.is_cash_accepted}
                            onChange={e => handleChange("is_cash_accepted", e.target.value)}
                        >
                            <option value={1}>Yes</option>
                            <option value={0}>No</option>
                        </Input>
                    </Col>
                    <Col md={6}>
                        <Label className="mt-3">Monthly Parking</Label>
                        <Input
                            type="select"
                            value={formData.is_monthly_parking_active}
                            onChange={e => handleChange("is_monthly_parking_active", e.target.value)}
                        >
                            <option value={1}>Yes</option>
                            <option value={0}>No</option>
                        </Input>
                    </Col>
                </Row>

                <Row>
                    <Col md={6}>
                        <Label className="mt-3">Manager Labor Rate</Label>
                        <InputGroup>
                            <InputGroupText>$</InputGroupText>
                            <Input
                                type="number"
                                value={formData.manager_labor_rate}
                                onChange={e => handleChange("manager_labor_rate", e.target.value)}
                            />
                        </InputGroup>
                    </Col>
                    <Col md={6}>
                        <Label className="mt-3">Attendant Labor Rate</Label>
                        <InputGroup>
                            <InputGroupText>$</InputGroupText>
                            <Input
                                type="number"
                                value={formData.attendant_labor_rate}
                                onChange={e => handleChange("attendant_labor_rate", e.target.value)}
                            />
                        </InputGroup>
                    </Col>
                </Row>

                <Label className="mt-3">Lease Amount</Label>
                <InputGroup>
                    <InputGroupText>$</InputGroupText>
                    <Input
                        type="number"
                        value={formData.lease_amount}
                        onChange={e => handleChange("lease_amount", e.target.value)}
                    />
                </InputGroup>

                <Label className="mt-3">Tax Status</Label>
                <Input
                    type="select"
                    value={formData.tax_status}
                    onChange={e => handleChange("tax_status", e.target.value)}
                >
                    <option value={null}>NONE</option>
                    <option value={"POT Tax"}>POT Tax</option>
                </Input>

                <Label className="mt-3">Is CurbAds Location?</Label>
                <Input
                    type="select"
                    value={formData.is_curbads_location}
                    onChange={e => handleChange("is_curbads_location", e.target.value)}
                >
                    <option value={1}>Yes</option>
                    <option value={0}>No</option>
                </Input>

                <Label className="mt-3">Partnership Remittance</Label>
                <Input
                    type="select"
                    value={formData.partner_remittance_terms}
                    onChange={e => handleChange("partner_remittance_terms", e.target.value)}
                >
                    <option value="">Select Remittance</option>
                    <option value="Flat Fee">Flat Fee</option>
                    <option value="Profit Share">Profit Share</option>
                    <option value="Revenue Share">Revenue Share</option>
                    <option value="Percentage After Expense">Percentage of Parking Sales After Covering Operating Expense</option>
                    <option value="No Remittance">No Remittance</option>
                </Input>

                {formData.partner_remittance_terms === "Flat Fee" && (
                    <div className="mt-3">
                        <Label>Flat Fee Amount ($)</Label>
                        <Input
                            type="number"
                            value={formData.partner_remittance_amount || ""}
                            onChange={e => handleChange("partner_remittance_amount", e.target.value)}
                        />
                    </div>
                )}

                {(formData.partner_remittance_terms === "Profit Share" ||
                    formData.partner_remittance_terms === "Revenue Share" ||
                    formData.partner_remittance_terms === "Percentage After Expense") && (
                        <div className="mt-3">
                            <Label>Percentage (%)</Label>
                            <Input
                                type="number"
                                value={formData.partner_remittance_percent || ""}
                                onChange={e => handleChange("partner_remittance_percent", e.target.value)}
                            />
                        </div>
                    )}

                <Label className="mt-3">Invoice Directives</Label>
                <Input
                    type="textarea"
                    rows="3"
                    value={formData.invoice_directives}
                    onChange={e => handleChange("invoice_directives", e.target.value)}
                />

                <Label className="mt-3">Third Party Platforms</Label>
                <Select
                    isMulti
                    styles={{
                        menu: (base) => ({ ...base, backgroundColor: 'white', zIndex: 9999 }),
                        menuPortal: base => ({ ...base, zIndex: 9999 })
                    }}
                    options={thirdPartyPlatforms.map(platform => ({
                        value: platform.id,
                        label: platform.name,
                    }))}
                    value={(formData.third_party_platforms || []).map(platform => ({
                        value: platform.id,
                        label: platform.name,
                    }))}
                    onChange={selectedOptions => {
                        // store full objects (id + name) in formData
                        handleChange(
                            "third_party_platforms",
                            selectedOptions.map(opt => ({
                                id: opt.value,
                                name: opt.label,
                            }))
                        );
                    }}
                />

                <Label className="mt-3">Parking Types</Label>
                <Select
                    isMulti
                    styles={{
                        menu: (base) => ({ ...base, backgroundColor: 'white', zIndex: 9999 }),
                        menuPortal: base => ({ ...base, zIndex: 9999 })
                    }}
                    options={parkingTypes.map(type => ({
                        value: type.id,
                        label: type.type,
                    }))}
                    value={(formData.parking_types || []).map(type => ({
                        value: type.id,
                        label: type.type,
                    }))}
                    onChange={selectedOptions => {
                        handleChange(
                            "parking_types",
                            selectedOptions.map(opt => ({
                                id: opt.value,
                                type: opt.label,
                            }))
                        );
                    }}
                />

                {/* Fees */}
                <Row>
                    <Col md={6}>
                        <Label className="mt-3">Management Fee</Label>
                        <InputGroup>
                            <InputGroupText>$</InputGroupText>
                            <Input
                                type="number"
                                value={formData.management_fee}
                                onChange={e => handleChange("management_fee", e.target.value)}
                            />
                        </InputGroup>
                    </Col>
                    <Col md={6}>
                        <Label className="mt-3">Insurance Fee</Label>
                        <InputGroup>
                            <InputGroupText>$</InputGroupText>
                            <Input
                                type="number"
                                value={formData.insurance_fee}
                                onChange={e => handleChange("insurance_fee", e.target.value)}
                            />
                        </InputGroup>
                    </Col>
                </Row>

                <Row>
                    <Col md={6}>
                        <Label className="mt-3">Parking Lot Fee</Label>
                        <InputGroup>
                            <InputGroupText>$</InputGroupText>
                            <Input
                                type="number"
                                value={formData.parking_lot_fee}
                                onChange={e => handleChange("parking_lot_fee", e.target.value)}
                            />
                        </InputGroup>
                    </Col>
                    <Col md={6}>
                        <Label className="mt-3">Permit/Meter Fee</Label>
                        <InputGroup>
                            <InputGroupText>$</InputGroupText>
                            <Input
                                type="number"
                                value={formData.permit_meter_fee}
                                onChange={e => handleChange("permit_meter_fee", e.target.value)}
                            />
                        </InputGroup>
                    </Col>
                </Row>

                <Row>
                    <Col md={6}>
                        <Label className="mt-3">Tech Fee</Label>
                        <InputGroup>
                            <InputGroupText>$</InputGroupText>
                            <Input
                                type="number"
                                value={formData.tech_fee}
                                onChange={e => handleChange("tech_fee", e.target.value)}
                            />
                        </InputGroup>
                    </Col>
                    <Col md={6}>
                        <Label className="mt-3">Partner Remittance Fee</Label>
                        <InputGroup>
                            <InputGroupText>$</InputGroupText>
                            <Input
                                type="number"
                                value={formData.partner_remittance_fee}
                                onChange={e => handleChange("partner_remittance_fee", e.target.value)}
                            />
                        </InputGroup>
                    </Col>
                </Row>

                <Row>
                    <Col md={6}>
                        <Label className="mt-3">Ops Supplies Fee</Label>
                        <InputGroup>
                            <InputGroupText>$</InputGroupText>
                            <Input
                                type="number"
                                value={formData.ops_supplies_fee}
                                onChange={e => handleChange("ops_supplies_fee", e.target.value)}
                            />
                        </InputGroup>
                    </Col>
                    <Col md={6}>
                        <Label className="mt-3">Parking Lot Cost</Label>
                        <InputGroup>
                            <InputGroupText>$</InputGroupText>
                            <Input
                                type="number"
                                value={formData.parking_lot_cost}
                                onChange={e => handleChange("parking_lot_cost", e.target.value)}
                            />
                        </InputGroup>
                    </Col>
                </Row>

                <Row>
                    <Col md={6}>
                        <Label className="mt-3">Labor Hours Cap</Label>
                        <InputGroup>
                            <Input
                                type="number"
                                value={formData.labor_hours_cap}
                                onChange={e => handleChange("labor_hours_cap", e.target.value)}
                            />
                            <InputGroupText>hrs</InputGroupText>
                        </InputGroup>
                    </Col>
                    <Col md={6}>
                        <Label className="mt-3">Spaces</Label>
                        <Input
                            type="number"
                            value={formData.location_spaces}
                            onChange={e => handleChange("location_spaces", e.target.value)}
                        />
                    </Col>
                </Row>

                <Label className="mt-3">Monthly Parking Capacity</Label>
                <Input
                    type="number"
                    value={formData.monthly_parking_capacity}
                    onChange={e => handleChange("monthly_parking_capacity", e.target.value)}
                />

                {/* Finance terms / Accounting Notes */}
                <Label className="mt-3 d-flex justify-content-between align-items-center">
                    Accounting Notes
                    <Button
                        size="sm"
                        color="success"
                        type="button"
                        onClick={() =>
                            handleChange("finance_terms", [
                                ...(formData.finance_terms || []),
                                { term: "" }
                            ])
                        }
                    >
                        + Add Note
                    </Button>
                </Label>

                {(formData.finance_terms || []).map((term, idx) => (
                    <div key={idx} className="d-flex align-items-center mb-2">
                        <Input
                            type="text"
                            value={term.term || ""}
                            onChange={e => {
                                const updated = [...formData.finance_terms];
                                updated[idx] = { ...updated[idx], term: e.target.value };
                                handleChange("finance_terms", updated);
                            }}
                        />
                        <Button
                            color="danger"
                            size="sm"
                            className="ms-2"
                            type="button"
                            onClick={() => {
                                const updated = [...formData.finance_terms];
                                updated.splice(idx, 1);
                                handleChange("finance_terms", updated);
                            }}
                        >
                            <i className="bx bx-trash"></i>
                        </Button>
                    </div>
                ))}

                {/* Add File Section */}
                <Label className="mt-3 d-flex justify-content-between align-items-center">
                    File Sections
                    <Button
                        size="sm"
                        color="success"
                        type="button"
                        onClick={() =>
                            setFormData(prev => ({
                                ...prev,
                                fileSections: [...prev.fileSections, { type: "", files: [] }]
                            }))
                        }
                    >
                        + Add File
                    </Button>
                </Label>

                {formData.fileSections.map((section, idx) => (
                    <Row key={idx} className="align-items-end mb-2">
                        <Col md={5}>
                            <Label>File Type</Label>
                            <Input
                                type="select"
                                value={section.type}
                                onChange={e => {
                                    const updated = [...formData.fileSections];
                                    updated[idx].type = e.target.value;
                                    handleChange("fileSections", updated);
                                }}
                            >
                                <option value="">Select File Type</option>
                                {fileTypes.map(opt => (
                                    <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </Input>
                        </Col>
                        <Col md={5}>
                            <Label>Upload File</Label>
                            <Input
                                type="file"
                                multiple
                                disabled={!section.type}
                                onChange={e => {
                                    const files = Array.from(e.target.files);
                                    const updated = [...formData.fileSections];
                                    updated[idx].files = [...(updated[idx].files || []), ...files];
                                    handleChange("fileSections", updated);
                                }}
                            />
                        </Col>
                        <Col md={2}>
                            <Button
                                color="danger"
                                size="sm"
                                onClick={() => {
                                    const updated = [...formData.fileSections];
                                    updated.splice(idx, 1);
                                    handleChange("fileSections", updated);
                                }}
                            >
                                <i className="bx bx-trash"></i>
                            </Button>
                        </Col>
                    </Row>
                ))}

                {/* Unified File Gallery */}
                {formData.fileSections.some(sec => sec.files.length > 0) && (
                    <div className="mt-3">
                        <Label>Files Gallery</Label>
                        <ul className="list-unstyled">
                            {formData.fileSections.map((sec, sIdx) =>
                                sec.files.map((file, fIdx) => (
                                    <li
                                        key={`${sIdx}-${fIdx}`}
                                        className="d-flex align-items-center justify-content-between mb-1"
                                    >
                                        <span>
                                            <strong>
                                                {fileTypes.find(ft => ft.value === sec.type)?.label || "Unknown"}:
                                            </strong>{" "}
                                            {file.name || file.file_name}
                                        </span>
                                        <Button
                                            color="danger"
                                            size="sm"
                                            onClick={() => {
                                                const updated = [...formData.fileSections];
                                                updated[sIdx].files.splice(fIdx, 1);
                                                handleChange("fileSections", updated);
                                            }}
                                        >
                                            <i className="bx bx-trash"></i>
                                        </Button>
                                    </li>
                                ))
                            )}
                        </ul>
                    </div>
                )}

                {/* Parking Lot Details */}
                <Label className="mt-3 d-flex justify-content-between align-items-center">
                    Parking Lot Details
                    <Button
                        size="sm"
                        color="success"
                        type="button"
                        onClick={() =>
                            setFormData(prev => ({
                                ...prev,
                                parking_lots: [
                                    ...(prev.parking_lots || []),
                                    {
                                        type: "",
                                        access_hours: "",
                                        rent_amount: "",
                                        due_date: "",
                                        payee_name: "",
                                        payee_email: "",
                                        payee_phone: "",
                                        spaces: "",
                                        rules: ""
                                    }
                                ]
                            }))
                        }
                    >
                        + Add Lot
                    </Button>
                </Label>

                {formData.parking_lots.map((lot, idx) => (
                    <div key={idx} className="border rounded p-3 mb-3">
                        <Row>
                            <Col md={6} className="mb-2">
                                <InputGroup>
                                    <InputGroupText>Type</InputGroupText>
                                    <Input
                                        type="select"
                                        value={lot.type}
                                        onChange={e => {
                                            const updated = [...formData.parking_lots];
                                            updated[idx].type = e.target.value;
                                            handleChange("parking_lots", updated);
                                        }}
                                    >
                                        <option value="">Select</option>
                                        <option value="Onsite">Onsite</option>
                                        <option value="Offsite">Offsite</option>
                                    </Input>
                                </InputGroup>
                            </Col>
                            <Col md={6} className="mb-2">
                                <InputGroup>
                                    <InputGroupText>Access Hours</InputGroupText>
                                    <Input
                                        type="text"
                                        value={lot.access_hours}
                                        onChange={e => {
                                            const updated = [...formData.parking_lots];
                                            updated[idx].access_hours = e.target.value;
                                            handleChange("parking_lots", updated);
                                        }}
                                    />
                                </InputGroup>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6} className="mb-2">
                                <InputGroup>
                                    <InputGroupText>$</InputGroupText>
                                    <Input
                                        type="text"
                                        placeholder="Rent Amount"
                                        value={lot.rent_amount}
                                        onChange={e => {
                                            const updated = [...formData.parking_lots];
                                            updated[idx].rent_amount = e.target.value;
                                            handleChange("parking_lots", updated);
                                        }}
                                    />
                                </InputGroup>
                            </Col>
                            <Col md={6} className="mb-2">
                                <InputGroup>
                                    <InputGroupText>Due Date</InputGroupText>
                                    <Input
                                        type="date"
                                        value={lot.due_date}
                                        onChange={e => {
                                            const updated = [...formData.parking_lots];
                                            updated[idx].due_date = e.target.value;
                                            handleChange("parking_lots", updated);
                                        }}
                                    />
                                </InputGroup>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={4} className="mb-2">
                                <InputGroup>
                                    <InputGroupText>Payee Name</InputGroupText>
                                    <Input
                                        type="text"
                                        value={lot.payee_name}
                                        onChange={e => {
                                            const updated = [...formData.parking_lots];
                                            updated[idx].payee_name = e.target.value;
                                            handleChange("parking_lots", updated);
                                        }}
                                    />
                                </InputGroup>
                            </Col>
                            <Col md={4} className="mb-2">
                                <InputGroup>
                                    <InputGroupText>Email</InputGroupText>
                                    <Input
                                        type="email"
                                        value={lot.payee_email}
                                        onChange={e => {
                                            const updated = [...formData.parking_lots];
                                            updated[idx].payee_email = e.target.value;
                                            handleChange("parking_lots", updated);
                                        }}
                                    />
                                </InputGroup>
                            </Col>
                            <Col md={4} className="mb-2">
                                <InputGroup>
                                    <InputGroupText>Phone</InputGroupText>
                                    <Input
                                        type="text"
                                        value={lot.payee_phone}
                                        onChange={e => {
                                            const updated = [...formData.parking_lots];
                                            updated[idx].payee_phone = e.target.value;
                                            handleChange("parking_lots", updated);
                                        }}
                                    />
                                </InputGroup>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6} className="mb-2">
                                <InputGroup>
                                    <InputGroupText>Spaces</InputGroupText>
                                    <Input
                                        type="number"
                                        value={lot.spaces}
                                        onChange={e => {
                                            const updated = [...formData.parking_lots];
                                            updated[idx].spaces = e.target.value;
                                            handleChange("parking_lots", updated);
                                        }}
                                    />
                                </InputGroup>
                            </Col>
                            <Col md={6} className="mb-2">
                                <InputGroup>
                                    <InputGroupText>Rules</InputGroupText>
                                    <Input
                                        type="textarea"
                                        value={lot.rules}
                                        onChange={e => {
                                            const updated = [...formData.parking_lots];
                                            updated[idx].rules = e.target.value;
                                            handleChange("parking_lots", updated);
                                        }}
                                    />
                                </InputGroup>
                            </Col>
                        </Row>

                        <Button
                            color="danger"
                            size="sm"
                            className="mt-2"
                            onClick={() => {
                                const updated = [...formData.parking_lots];
                                updated.splice(idx, 1);
                                handleChange("parking_lots", updated);
                            }}
                        >
                            <i className="bx bx-trash"></i> Remove Lot
                        </Button>
                    </div>
                ))}
            </ModalBody>
            <ModalFooter>
                <Button className="button-gradient-bg" onClick={onSave} disabled={isSaving}>
                    {isSaving ? "Saving..." : "Save"}
                </Button>
                <Button color="secondary" onClick={toggle} disabled={isSaving}>
                    Cancel
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default AddLocationModal;